import React, { useState } from 'react';
import ImageUploader from './ImageUploader';

const QuestionEditor = ({ questions, onUpdate, metadata }) => {
  const [activeTab, setActiveTab] = useState('preview'); // preview | edit | json
  const [editableQuestions, setEditableQuestions] = useState([...questions]);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [expandedQuestions, setExpandedQuestions] = useState(new Set());

  // Toggle question expansion
  const toggleExpand = (index) => {
    const newExpanded = new Set(expandedQuestions);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedQuestions(newExpanded);
  };

  // Update question field
  const updateQuestion = (index, field, value) => {
    const updated = [...editableQuestions];
    updated[index] = { ...updated[index], [field]: value };
    setEditableQuestions(updated);
    onUpdate(updated);
  };

  // Delete question
  const deleteQuestion = (index) => {
    if (window.confirm('დარწმუნებული ხართ, რომ გსურთ ამ კითხვის წაშლა?')) {
      const updated = editableQuestions.filter((_, i) => i !== index);
      // Re-number questions
      const renumbered = updated.map((q, i) => ({ ...q, id: i + 1 }));
      setEditableQuestions(renumbered);
      onUpdate(renumbered);
    }
  };

  // Add new question
  const addQuestion = () => {
    const newQuestion = {
      id: editableQuestions.length + 1,
      text: '',
      answer: '',
      author: 'Unknown',
      comment: '',
      source: '',
      url: '',
      image: null
    };
    const updated = [...editableQuestions, newQuestion];
    setEditableQuestions(updated);
    setExpandedQuestions(new Set([...expandedQuestions, updated.length - 1]));
    onUpdate(updated);
  };

  // Drag and drop handlers
  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const updated = [...editableQuestions];
    const draggedItem = updated[draggedIndex];
    updated.splice(draggedIndex, 1);
    updated.splice(index, 0, draggedItem);

    // Re-number
    const renumbered = updated.map((q, i) => ({ ...q, id: i + 1 }));
    
    setEditableQuestions(renumbered);
    setDraggedIndex(index);
    onUpdate(renumbered);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // Validation
  const isQuestionValid = (question) => {
    return question.text.trim() !== '' && question.answer.trim() !== '';
  };

  const getInvalidCount = () => {
    return editableQuestions.filter(q => !isQuestionValid(q)).length;
  };

  // Render tabs
  const renderTabs = () => (
    <div style={styles.tabs}>
      <button
        style={{
          ...styles.tab,
          ...(activeTab === 'preview' ? styles.activeTab : {})
        }}
        onClick={() => setActiveTab('preview')}
      >
        📊 გადახედვა
      </button>
      <button
        style={{
          ...styles.tab,
          ...(activeTab === 'edit' ? styles.activeTab : {})
        }}
        onClick={() => setActiveTab('edit')}
      >
        ✏️ რედაქტირება ({editableQuestions.length})
      </button>
      <button
        style={{
          ...styles.tab,
          ...(activeTab === 'json' ? styles.activeTab : {})
        }}
        onClick={() => setActiveTab('json')}
      >
        📄 JSON
      </button>
    </div>
  );

  // Render preview tab
  const renderPreview = () => (
    <div style={styles.previewContainer}>
      <h3>პირველი 5 კითხვა</h3>
      {editableQuestions.slice(0, 5).map((q, idx) => (
        <div key={idx} style={styles.previewCard}>
          <div style={styles.previewHeader}>
            <strong>კითხვა {q.id}</strong>
            {!isQuestionValid(q) && <span style={styles.invalidBadge}>⚠️ არასრული</span>}
          </div>
          
          <div style={styles.previewText}>
            <strong>ტექსტი:</strong> {q.text || <em style={{color: '#999'}}>ცარიელი</em>}
          </div>
          
          <div style={styles.previewText}>
            <strong>პასუხი:</strong> {q.answer || <em style={{color: '#999'}}>ცარიელი</em>}
          </div>
          
          {q.author && q.author !== 'Unknown' && (
            <div style={styles.previewText}><strong>ავტორი:</strong> {q.author}</div>
          )}
          
          {q.comment && (
            <div style={styles.previewText}>
              <strong>კომენტარი:</strong> {q.comment.length > 100 ? q.comment.substring(0, 100) + '...' : q.comment}
            </div>
          )}
          
          {q.source && (
            <div style={styles.previewText}><strong>წყარო:</strong> {q.source}</div>
          )}
          
          {q.url && (
            <div style={styles.previewText}>
              <strong>URL:</strong>{' '}
              <a href={q.url} target="_blank" rel="noopener noreferrer" style={{color: '#667eea'}}>
                {q.url.length > 50 ? q.url.substring(0, 50) + '...' : q.url}
              </a>
            </div>
          )}
          
          {q.image && (
            <div style={styles.previewText}>
              <strong>სურათი:</strong>
              <div style={{marginTop: '10px'}}>
                <img 
                  src={q.image} 
                  alt="Question" 
                  style={styles.previewImage}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
                <span style={{display: 'none', color: '#999'}}>
                  {q.image.length > 60 ? q.image.substring(0, 60) + '...' : q.image}
                </span>
              </div>
            </div>
          )}
        </div>
      ))}
      {editableQuestions.length > 5 && (
        <p style={{textAlign: 'center', color: '#666', marginTop: '20px'}}>
          და კიდევ {editableQuestions.length - 5} კითხვა...
        </p>
      )}
    </div>
  );

  // Render edit tab
  const renderEdit = () => (
    <div style={styles.editContainer}>
      <div style={styles.editHeader}>
        <div>
          <h3>კითხვების რედაქტირება</h3>
          {getInvalidCount() > 0 && (
            <p style={styles.warningText}>
              ⚠️ {getInvalidCount()} არასრული კითხვა (ტექსტი ან პასუხი ცარიელია)
            </p>
          )}
        </div>
        <button style={styles.addButton} onClick={addQuestion}>
          ➕ ახალი კითხვა
        </button>
      </div>

      <div style={styles.questionsList}>
        {editableQuestions.map((q, index) => (
          <div
            key={index}
            draggable
            onDragStart={() => handleDragStart(index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragEnd={handleDragEnd}
            style={{
              ...styles.questionCard,
              ...(draggedIndex === index ? styles.dragging : {}),
              ...(isQuestionValid(q) ? {} : styles.invalidCard)
            }}
          >
            {/* Header */}
            <div style={styles.questionHeader}>
              <div style={styles.questionHeaderLeft}>
                <span style={styles.dragHandle}>⋮⋮</span>
                <strong>კითხვა {q.id}</strong>
                {!isQuestionValid(q) && <span style={styles.invalidBadge}>⚠️ არასრული</span>}
              </div>
              <div style={styles.questionHeaderRight}>
                <button
                  style={styles.expandButton}
                  onClick={() => toggleExpand(index)}
                >
                  {expandedQuestions.has(index) ? '▼' : '▶'}
                </button>
                <button
                  style={styles.deleteButton}
                  onClick={() => deleteQuestion(index)}
                >
                  🗑️
                </button>
              </div>
            </div>

            {/* Collapsed preview */}
            {!expandedQuestions.has(index) && (
              <div style={styles.collapsedPreview}>
                <div><strong>ტექსტი:</strong> {q.text.substring(0, 80) || <em style={{color: '#c33'}}>ცარიელი</em>}...</div>
                <div><strong>პასუხი:</strong> {q.answer || <em style={{color: '#c33'}}>ცარიელი</em>}</div>
                {q.author && q.author !== 'Unknown' && (
                  <div><strong>ავტორი:</strong> {q.author}</div>
                )}
                {q.comment && (
                  <div><strong>კომენტარი:</strong> {q.comment.substring(0, 50)}...</div>
                )}
                {q.source && (
                  <div><strong>წყარო:</strong> {q.source.substring(0, 50)}...</div>
                )}
                {q.url && (
                  <div><strong>URL:</strong> {q.url.substring(0, 40)}...</div>
                )}
                {q.image && (
                  <div><strong>სურათი:</strong> ✓</div>
                )}
              </div>
            )}

            {/* Expanded form */}
            {expandedQuestions.has(index) && (
              <div style={styles.questionForm}>
                {/* Question Text */}
                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    კითხვის ტექსტი <span style={styles.required}>*</span>
                  </label>
                  <textarea
                    style={{
                      ...styles.textarea,
                      ...(q.text.trim() === '' ? styles.invalidInput : {})
                    }}
                    value={q.text}
                    onChange={(e) => updateQuestion(index, 'text', e.target.value)}
                    rows={4}
                    placeholder="შეიყვანეთ კითხვის ტექსტი..."
                  />
                </div>

                {/* Answer */}
                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    პასუხი <span style={styles.required}>*</span>
                  </label>
                  <input
                    style={{
                      ...styles.input,
                      ...(q.answer.trim() === '' ? styles.invalidInput : {})
                    }}
                    type="text"
                    value={q.answer}
                    onChange={(e) => updateQuestion(index, 'answer', e.target.value)}
                    placeholder="შეიყვანეთ პასუხი..."
                  />
                </div>

                {/* Author */}
                <div style={styles.formGroup}>
                  <label style={styles.label}>ავტორი</label>
                  <input
                    style={styles.input}
                    type="text"
                    value={q.author}
                    onChange={(e) => updateQuestion(index, 'author', e.target.value)}
                    placeholder="ავტორის სახელი..."
                  />
                </div>

                {/* Comment */}
                <div style={styles.formGroup}>
                  <label style={styles.label}>კომენტარი</label>
                  <textarea
                    style={styles.textarea}
                    value={q.comment}
                    onChange={(e) => updateQuestion(index, 'comment', e.target.value)}
                    rows={3}
                    placeholder="კომენტარი (არასავალდებულო)..."
                  />
                </div>

                {/* Source */}
                <div style={styles.formGroup}>
                  <label style={styles.label}>წყარო</label>
                  <input
                    style={styles.input}
                    type="text"
                    value={q.source}
                    onChange={(e) => updateQuestion(index, 'source', e.target.value)}
                    placeholder="წყაროს აღწერა..."
                  />
                </div>

                {/* URL */}
                <div style={styles.formGroup}>
                  <label style={styles.label}>URL</label>
                  <input
                    style={styles.input}
                    type="url"
                    value={q.url}
                    onChange={(e) => updateQuestion(index, 'url', e.target.value)}
                    placeholder="https://..."
                  />
                </div>

                {/* Image Upload */}
                <ImageUploader
                  currentImageUrl={q.image}
                  packetId={metadata?.id || 'temp'}
                  questionNumber={q.id}
                  onImageUploaded={(url) => updateQuestion(index, 'image', url)}
                  onImageRemoved={() => updateQuestion(index, 'image', null)}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  // Render JSON tab
  const renderJSON = () => {
    const jsonData = {
      ...metadata,
      questions: editableQuestions.length,
      questions_data: editableQuestions
    };

    return (
      <div style={styles.jsonContainer}>
        <div style={styles.jsonHeader}>
          <h3>JSON გადახედვა</h3>
          <button
            style={styles.copyButton}
            onClick={() => {
              navigator.clipboard.writeText(JSON.stringify(jsonData, null, 2));
              alert('JSON დაკოპირდა!');
            }}
          >
            📋 კოპირება
          </button>
        </div>
        <pre style={styles.jsonPre}>
          {JSON.stringify(jsonData, null, 2)}
        </pre>
      </div>
    );
  };

  return (
    <div style={styles.container}>
      {renderTabs()}
      <div style={styles.content}>
        {activeTab === 'preview' && renderPreview()}
        {activeTab === 'edit' && renderEdit()}
        {activeTab === 'json' && renderJSON()}
      </div>
    </div>
  );
};

const styles = {
  container: {
    marginTop: '30px',
  },
  tabs: {
    display: 'flex',
    gap: '10px',
    borderBottom: '2px solid #eee',
    marginBottom: '20px',
  },
  tab: {
    padding: '12px 24px',
    background: 'transparent',
    border: 'none',
    borderBottom: '3px solid transparent',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '500',
    color: '#666',
    transition: 'all 0.2s',
  },
  activeTab: {
    borderBottom: '3px solid #667eea',
    color: '#667eea',
    fontWeight: 'bold',
  },
  content: {
    minHeight: '400px',
  },
  
  // Preview styles
  previewContainer: {
    padding: '10px',
  },
  previewCard: {
    background: '#f9f9f9',
    padding: '15px',
    borderRadius: '8px',
    marginBottom: '15px',
    border: '1px solid #e0e0e0',
  },
  previewHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px',
  },
  previewText: {
    marginBottom: '8px',
    lineHeight: '1.5',
  },
  
  // Edit styles
  editContainer: {
    padding: '10px',
  },
  editHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '20px',
  },
  warningText: {
    color: '#d32f2f',
    margin: '5px 0',
    fontSize: '14px',
  },
  addButton: {
    background: '#4caf50',
    color: 'white',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold',
  },
  questionsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
  },
  questionCard: {
    background: 'white',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    padding: '15px',
    cursor: 'move',
    transition: 'all 0.2s',
  },
  invalidCard: {
    borderColor: '#f44336',
    background: '#fff5f5',
  },
  dragging: {
    opacity: 0.5,
    transform: 'scale(0.98)',
  },
  questionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px',
  },
  questionHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  questionHeaderRight: {
    display: 'flex',
    gap: '10px',
  },
  dragHandle: {
    fontSize: '20px',
    color: '#999',
    cursor: 'move',
  },
  expandButton: {
    background: '#667eea',
    color: 'white',
    border: 'none',
    padding: '5px 12px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
  },
  deleteButton: {
    background: '#f44336',
    color: 'white',
    border: 'none',
    padding: '5px 12px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
  },
  invalidBadge: {
    background: '#f44336',
    color: 'white',
    padding: '3px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 'bold',
  },
  collapsedPreview: {
    padding: '10px',
    background: '#f9f9f9',
    borderRadius: '4px',
  },
  questionForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
  },
  label: {
    marginBottom: '5px',
    fontWeight: '500',
    fontSize: '14px',
  },
  required: {
    color: '#f44336',
  },
  input: {
    padding: '10px',
    border: '2px solid #ddd',
    borderRadius: '6px',
    fontSize: '14px',
  },
  textarea: {
    padding: '10px',
    border: '2px solid #ddd',
    borderRadius: '6px',
    fontSize: '14px',
    fontFamily: 'inherit',
    resize: 'vertical',
  },
  invalidInput: {
    borderColor: '#f44336',
    background: '#fff5f5',
  },
  
  // Preview tab image styles
  previewImage: {
    maxWidth: '100%',
    maxHeight: '200px',
    borderRadius: '8px',
    border: '1px solid #ddd',
    marginTop: '5px',
  },
  
  // JSON styles
  jsonContainer: {
    padding: '10px',
  },
  jsonHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '15px',
  },
  copyButton: {
    background: '#667eea',
    color: 'white',
    border: 'none',
    padding: '8px 16px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
  },
  jsonPre: {
    background: '#1e1e1e',
    color: '#d4d4d4',
    padding: '20px',
    borderRadius: '8px',
    overflow: 'auto',
    fontFamily: "'Courier New', monospace",
    fontSize: '13px',
    maxHeight: '600px',
  },
};

export default QuestionEditor;